import {
  asArray,
  isObj,
  mapImage,
  mapLink,
  mapText,
  mapTitle,
  normalizeSpacer,
  pickStr,
} from "@/components/DesignSystem/utils/teaserHelper"
import { handleLearnMoreClick } from "@/components/DesignSystem/analytics/compareSliderAnalytics"
import { sanitizeTextForAnalytics } from "@/utils/helper"
import { validateCompareSliderProps } from "../validators/compareSliderValidator"
import { Image } from "@kohler-dsm-library/kohler-core"
import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"

/**
 * Maps a "Compare Slider" (AEM `mediacontentmodule`) container into `ICompareSliderProps`.
 *
 * Picks the first child by `:itemsOrder` (or first key in `:items`), then maps:
 * - `heading` via `mapTitle(basic.heading)`
 * - `CTA` via the first item of `basic.cta` (if any)
 * - `cards[]` from `card.cards` (array or single object), where each card includes:
 *    - `image` via `mapImage(cardItem.image)`
 *    - `title` via `mapTitle(cardItem.title)`
 *    - `description` via `mapText(cardItem.description)` (optional)
 *    - `price` via `mapText(cardItem.price)` (optional)
 *    - `features` via `mapText` for each feature item (optional)
 *    - `colorSwatches` (optional): maps `name` and `colors[]` (keeps { colorHex, colorName, value })
 *    - `CTA` via the first item of `cardItem.cta` (optional)
 * - `topSpacer` / `bottomSpacer` via `normalizeSpacer` (if present on the node/container)
 *
 * Omits keys with empty/invalid values. Returns `null` if no valid child node is found
 * or if no valid `cards` can be produced.
 *
 * @param {Object} container - The container object, possibly with style/class info.
 * @return {Object} Mapped props including heading, description, cards, and background.
 */
export function mapCompareSliderProps(container) {
  try {
    if (!container || !isObj(container)) return null

    const firstKey =
      asArray(container[":itemsOrder"])?.[0] ||
      (container[":items"] ? Object.keys(container[":items"])[0] : undefined)

    const node = firstKey ? container[":items"]?.[firstKey] : undefined
    if (!node || !isObj(node)) return null

    const basic = node.basic || {}
    const heading = mapTitle(basic.heading)
    const CTA = mapLink(asArray(basic.cta)?.[0])
    const topSpacer = basic.topSpacer
      ? normalizeSpacer(basic.topSpacer.size)
      : undefined
    const bottomSpacer = basic.bottomSpacer
      ? normalizeSpacer(basic.bottomSpacer.size)
      : undefined

    const rawCards = node.card?.cards
    const cardsArr = asArray(rawCards)
      .map(cardItem => {
        if (!cardItem || !isObj(cardItem)) return null

        const image = mapImage(cardItem.image?.[0] || cardItem.image)
        const title = mapTitle(cardItem.title?.[0])

        if (!image || !title) return null
        const cardCTA = mapLink(cardItem.cta?.[0])

        if (cardCTA) {
          cardCTA.onClick = () =>
            handleLearnMoreClick(sanitizeTextForAnalytics(title.text))
          cardCTA.target = cardCTA?.isNewTab ? "_blank" : "_self"
          if (cardCTA.target === "_blank") cardCTA.rel = "noopener noreferrer"
          if (cardCTA.href) cardCTA.href = modifyHref(cardCTA.href)
        }

        const description = mapText(cardItem.description?.[0])
        const price = mapText(cardItem.price?.[0])

        const features = asArray(cardItem.features)
          .map(f => {
            const featureProps = mapText(f)
            if (f.featureicon) {
              featureProps.icon = (
                <Image
                  src={f.featureicon}
                  width={20}
                  height={20}
                  alt={f.featureAltText}
                  reveal="default"
                  loading="blank"
                />
              )
            }
            return featureProps
          })
          .filter(Boolean)

        let colorSwatches = undefined
        const rawColorSwatches =
          cardItem.colorSwatches?.[0] || cardItem.colorSwatches
        if (rawColorSwatches && isObj(rawColorSwatches)) {
          const name = pickStr(rawColorSwatches.name)
          const colors = asArray(rawColorSwatches.colors)
            .map(c => {
              if (!c || !isObj(c)) return null
              const colorHex = pickStr(c.colorHex)
              const colorName = pickStr(c.colorName)
              const value = pickStr(c.value) || pickStr(c.colorName)
              if (!colorHex && !value) return null
              return {
                ...(colorHex ? { colorHex } : {}),
                ...(colorName ? { colorName } : {}),
                ...(value ? { value } : {}),
              }
            })
            .filter(Boolean)

          if (name || (colors && colors.length)) {
            colorSwatches = { name, colors }
          }
        }

        return {
          image,
          title,
          ...(description ? { description } : {}),
          ...(price ? { price } : {}),
          ...(features && features.length ? { features } : {}),
          ...(colorSwatches ? { colorSwatches } : {}),
          ...(cardCTA ? { CTA: cardCTA } : {}),
        }
      })
      .filter(Boolean)

    if (!cardsArr.length) return null

    const compareSliderProps = {
      heading,
      cards: cardsArr,
      ...(CTA ? { CTA } : {}),
      ...(topSpacer ? { topSpacer } : {}),
      ...(bottomSpacer ? { bottomSpacer } : {}),
    }

    const { isValid, errors } = validateCompareSliderProps(compareSliderProps)

    if (!isValid) {
      console.warn("compareSliderProps validation errors:", errors)
      return null
    }

    return compareSliderProps
  } catch (error) {
    console.error("Error in mapCompareSliderProps:", error)
    return null
  }
}

export default mapCompareSliderProps
